/* eslint-disable no-undef */
import aesjs from 'aes-js'
import forge from 'node-forge'
import JSencrypt from 'jsencrypt'

import { encryptCardData, isEncryptionEnabled } from './cardEncryption'

const { pki } = forge

const rsa_ = new JSencrypt()

export const generateAESPairs = () => {
    let key = []
    let iv
    for (let k = 0; k < 16; k++) {
        key.push(Math.floor(Math.random() * 255))
    }
    for (let k = 0; k < 16; k++) {
        iv = Math.floor(Math.random() * 255)
    }

    return {
        k: key,
        s: iv
    }
}

/**
 * Devuelve el nombre del campo que trae el PAN, o null si el payload no lleva
 * tarjeta.
 *
 * @param {object} [card] - Tarjeta del payload
 * @returns {string|null} `cardNumber`, `number` o null
 */
const findPanField = (card) => {
    if (!card) return null
    if (card.cardNumber) return 'cardNumber'
    if (card.number) return 'number'
    return null
}

/**
 * Empaqueta el payload en base64 (`ed`), reemplazando el PAN y el CVV por un JWE
 * en `card.encryptedCard` cuando el comercio tiene el cifrado en origen
 * habilitado.
 *
 * @param {object} obj - Payload a enviar, con la tarjeta en `card`
 * @param {string} session - Identificador de la sesión
 * @param {object} [encryption] - Bloque `encryption` de la sesión
 * @param {string} [orderId] - Referencia de la orden que se sella en el JWE
 * @returns {Promise<{session: string, ed: string}>}
 * @throws Si el cifrado falla; no hay fallback a texto plano
 */
export const encryptV2 = async (obj, session, encryption, orderId) => {
    let payload = obj
    const panField = isEncryptionEnabled(encryption)
        ? findPanField(obj?.card)
        : null

    if (panField) {
        const encryptedCard = await encryptCardData(encryption, {
            number: obj.card[panField],
            cvv: obj.card.cvc,
            orderId
        })
        const card = { ...obj.card, encryptedCard }
        delete card[panField]
        delete card.cvc
        payload = { ...obj, card }
    }

    try {
        // INFORMACION PROTEGIDA
        const message =
            typeof payload !== 'string' ? JSON.stringify(payload) : payload

        const encryptedData = Buffer.from(message).toString('base64')

        return { session, ed: encryptedData }
    } catch (e) {
        console.error(`Failed to encrypt: ${e.message}`)
    }
}

export const pack = (obj, session, pair_) => {
    let pair = pair_ !== undefined ? pair_ : generateAESPairs()

    let textBytes = aesjs.utils.utf8.toBytes(JSON.stringify(obj))
    let aesCtr = new aesjs.ModeOfOperation.ctr(
        pair.k,
        new aesjs.Counter(pair.s)
    )
    let encryptedBytes = aesCtr.encrypt(textBytes)
    let encryptedHex = aesjs.utils.hex.fromBytes(encryptedBytes)

    return {
        session: session,
        ld: encryptedHex,
        lk: rsa_.encrypt(JSON.stringify(pair))
    }
}

export const createPublicKey = (pem) => {
    let privateKey = pki.privateKeyFromPem(pem)
    let publicKey = pki.setRsaPublicKey(privateKey.n, privateKey.e)
    return pki.publicKeyToPem(publicKey)
}

export const setPublicKey = (pk) => {
    rsa_.setPublicKey(pk)
}
